import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { useMemo, useEffect } from 'react';
import { getValidationSchema } from '../../util/helpers/getValidationSchema';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import useStepPost from '../../hooks/useStepPost';
import { useMetalStreamData } from '../../hooks/useMetalStreamData';
import { metalStreamDataHelper } from '../../util/helpers/metalStreamDataHelper';
import useMetalStreamAmountDispatch from '../../hooks/useMetalStreamAmountDispatch';
import { FormWrap } from '../FormContainer/FormContainer';
import OnBoardingCta from '../OnBoarding/OnBoardingCta';
import CurrencyInput from '../CurrencyInput';
import Paragraph from '../Paragraph/Paragraph';
import { formatMoney } from '../../util/helpers/numeralHelpers';
import themeColors from '../../assets/styles/themeColors';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';

const MetalStreamAmountForm = ({ nextPath, onModalClose }) => {
  const { t } = useTranslation();
  const {
    MetaltreamMinimumDepositAmount,
    MetalStreamMaximumDepositAmount,
  } = useSelector(selectSettings);
  const { MetalStreamData } = useMetalStreamData();
  const { metalStreamAmountDispatch } = useMetalStreamAmountDispatch();
  const { onSuccess } = useStepPost({ nextPath, onModalClose });
  const { gtmStepEvent } = useGtmStepHook();

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'MetalStream invest amount',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'MetalStream invest amount',
    });
  };

  const formValidationSchema = useMemo(
    () =>
      Yup.object({
        ...getValidationSchema({
          fields: ['Amount'],
          minAmount: MetaltreamMinimumDepositAmount,
          maxAmount: MetalStreamMaximumDepositAmount,
        }),
      }),
    [MetaltreamMinimumDepositAmount, MetalStreamMaximumDepositAmount],
  );

  const { handleSubmit, control, setError } = useForm({
    resolver: yupResolver(formValidationSchema),
    defaultValues: {
      Amount: metalStreamDataHelper(MetalStreamData, 'Amount') || 0,
    },
  });

  const onSubmit = ({ Amount }) => {
    const onError = (errorMessage) => {
      setError('Amount', errorMessage);
    };

    metalStreamAmountDispatch({
      data: {
        Amount,
      },
      onSuccess,
      onError,
    });
    handleGtmEvent();
  };

  return (
    <FormWrap>
      <Controller
        control={control}
        render={({ field, fieldState }) => (
          <CurrencyInput
            label={t('metalstream.amountField')}
            {...field}
            {...fieldState}
            autoFocus
          />
        )}
        name="Amount"
      />
      <Paragraph color={themeColors.colorTextPrimary} variant="normal">
        {t('fundAccount.limitationCopyMetalStream', {
          maxAmount: formatMoney(MetalStreamMaximumDepositAmount),
        })}
      </Paragraph>
      <OnBoardingCta type="submit" onClick={handleSubmit(onSubmit)} />
    </FormWrap>
  );
};

MetalStreamAmountForm.propTypes = {
  nextPath: PropTypes.string,
  onModalClose: PropTypes.func,
};

export default MetalStreamAmountForm;
